import React, { createContext, useEffect, useState, useRef } from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import { Helmet } from "react-helmet";
import metaOpenGraph from "../utils/meta-open-graph";
import BackToTop from "@unum/back-to-top";
import Root from "@unum/root";
import { ClickableAction } from "@unum/clickable";
import NotificationBanner from "@unum/notification-banner";
import { useCookie } from "@unum/hooks";
import RichText, { checkValueExists } from "./RichText";
import MicroCopy from "./MicroCopy";
import Footer from "./Footer";
import NavStrip from "./NavStrip";
import PrimaryNavStrip from "./PrimaryNavStrip";
import CookieBanner from "./CookieBanner";
import SkipToContent from "@unum/skip-to-content";
import {
  LayoutQueryQuery,
  BranchFragmentFragment,
  RichTextFragmentFragment
} from "../../graphql-types";
import KontentSmartLink from "@kontent-ai/smart-link";
import { ShareImages } from "../utils/meta-open-graph";
import removeMd from "remove-markdown";
import { convert } from "html-to-text";
import Breadcrumb from "@unum/breadcrumb";
import type { BreadcrumbItem } from "@unum/breadcrumb";

export type Location = {
  key: string;
  pathname: string;
  search: string;
  hash: string;
  state: Record<string, any>;
};

type Props = {
  title: string;
  metaTitle?: string;
  description?: string;
  children: React.ReactNode;
  location: Location;
  branch?: BranchFragmentFragment;
  footerText?: RichTextFragmentFragment;
  initialisePreview?: boolean;
  hasNoIndex?: boolean;
  isStandaloneHeader?: boolean;
  isStandaloneFooter?: boolean;
  shareImages?: ShareImages;
  breadCrumbItems?: BreadcrumbItem[];
} & Omit<React.HTMLAttributes<HTMLDivElement>, "title">;

type Context = {
  location?: Location;
};

export const LayoutContext = createContext<Context>({});

const Layout = ({
  title,
  metaTitle,
  description,
  children,
  location,
  branch,
  footerText,
  initialisePreview = false,
  hasNoIndex = false,
  isStandaloneHeader = false,
  isStandaloneFooter = false,
  breadCrumbItems,
  shareImages,
  ...rest
}: Props) => {
  useEffect(() => {
    if (!initialisePreview) {
      return;
    }

    const plugin = KontentSmartLink.initialize({
      queryParam: "preview-mode"
    });

    return () => {
      plugin.destroy();
    };
  });
  const [notificationCookie, setNotificationCookie] = useCookie(
    "notification-banner"
  );
  const [showNotificationBanner, setShowNotificationBanner] =
    useState<boolean>(false);
  const layoutQueryResult = useStaticQuery<LayoutQueryQuery>(query);
  const site = layoutQueryResult?.allKontentItemSite?.nodes[0];
  const siteURL = layoutQueryResult?.site?.siteMetadata?.siteURL;

  useEffect(() => {
    setShowNotificationBanner(!notificationCookie);
  }, [notificationCookie]);

  const {
    navigation,
    navigation_footer,
    navigation_promo,
    navigation_promo_footer
  } = branch?.elements || {};

  const navStripData = {
    mainNavigation: site?.elements?.main_navigation.value[0],
    branches: site?.elements?.branches.value,
    loginMenu: site?.elements?.login_menu.value[0]
  };
  const navigationData = {
    mainNavigation: isStandaloneHeader ? undefined : navigation?.value[0],
    navigationFooter: navigation_footer?.value[0],
    navigationPromo: navigation_promo?.value[0],
    navigationPromoFooter: navigation_promo_footer
  };
  const logoAction: ClickableAction = isStandaloneHeader
    ? { model: "none" }
    : { model: "routerLink", to: "/", linkComponent: Link };

  const skipTarget = useRef(null);

  return (
    <div
      data-kontent-project-id={process.env.GATSBY_SMART_LINK_PROJECT_ID}
      data-kontent-language-codename="default"
    >
      <LayoutContext.Provider value={{ location }}>
        <Helmet htmlAttributes={{ lang: "en" }}>
          <script>
            {
              /* Google consent mode defaults */
              `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}

                gtag('consent', 'default', {
                  'ad_storage': 'denied',
                  'ad_user_data': 'denied',
                  'ad_personalization': 'denied',
                  'analytics_storage': 'denied'
                });
              `
            }
          </script>
          <title>{removeMd(title)}</title>
          {description ? (
            <meta name="description" content={convert(description)} />
          ) : null}
          {hasNoIndex ? <meta name="robots" content="noindex" /> : null}
          {metaOpenGraph({
            title: metaTitle || title,
            url: `${siteURL}${location.pathname}`,
            description: convert(description),
            shareImages: shareImages
          }).map(({ attribute: { key, value }, content }, index) => (
            <meta key={index} {...{ [key]: value }} content={content} />
          ))}
        </Helmet>
        <Root>
          <MicroCopy>
            <SkipToContent target={skipTarget} />
            {checkValueExists(site?.elements.notification_banner.value) &&
              showNotificationBanner && (
                <NotificationBanner
                  onDismissClick={() => setNotificationCookie(true)}
                >
                  <RichText data={site.elements.notification_banner} />
                </NotificationBanner>
              )}
            {site && (
              <div className="SiteHeader">
                {!isStandaloneHeader && <NavStrip data={navStripData} />}
                <PrimaryNavStrip
                  data={navigationData}
                  logoAction={logoAction}
                  renderSearch={!isStandaloneHeader}
                />
                {!isStandaloneHeader && breadCrumbItems && (
                  <Breadcrumb items={breadCrumbItems} />
                )}
              </div>
            )}
            <div {...rest} ref={skipTarget}>
              {children}
              {site && !isStandaloneFooter && (
                <Footer
                  data={site.elements?.footer_navigation.value[0]}
                  footerText={footerText}
                />
              )}
              <CookieBanner />
            </div>
          </MicroCopy>
        </Root>
        {typeof window !== "undefined" && (
          <BackToTop appearOn={window.innerHeight || 500} />
        )}
      </LayoutContext.Provider>
    </div>
  );
};

export default Layout;

export const query = graphql`
  query LayoutQuery {
    allKontentItemSite {
      nodes {
        id
        elements {
          main_navigation {
            value {
              ...MenuFragment
            }
          }
          footer_navigation {
            value {
              ...FooterFragment
            }
          }
          branches {
            value {
              ...NavStripBranchesFragment
            }
          }
          login_menu {
            value {
              ...MenuFragment
            }
          }
          notification_banner {
            ...RichTextFragment
          }
        }
      }
    }
    site {
      siteMetadata {
        siteURL
      }
    }
  }

  fragment BranchFragment on kontent_item_branch {
    elements {
      navigation {
        value {
          ...MainNavigationFragment
        }
      }
      navigation_footer {
        value {
          ...NavigationFooterFragment
        }
      }
      navigation_promo {
        value {
          ...TitleWithContentFragment
        }
      }
      navigation_promo_footer {
        ...RichTextFragment
      }
    }
  }
`;
